import React, { useCallback, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { State, useAppDispatch } from "../../../../state/store";
import { getCareRecipientInfoAction, updateCareRecipientInfoAction } from "../../../../state/usecase/admin/care_recipient/actions";
import { Loading } from "../../common/Loading";
import { CareRecipientEditComponent, CareRecipientEditComponentProps } from "./CareRecipientEditComponent";
import { CareRecipientName } from "../../../../domain/care_recipient/CareRecipientName";
import { DateObject } from "../../../../domain/fundamental/DateObject";
import { CareRecipientHouseId } from "../../../../domain/care_recipient/CareRecipientHouseId";
import { ServiceProviderId } from "../../../../domain/service_provider/ServiceProviderId";
import { DateTime } from "../../../../domain/fundamental/DateTime";
import { BackButton } from "../../common/button/BackButton";
import { getSensorFromValue } from "../../../../domain/care_recipient/Sensor";
import { useCareRecipientInformation } from "../../../hooks/admin/care_recipient/useCareRecipientInformation";
import { useCareRecipientIdFromPath } from "../../../hooks/care_recipient/useCareRecipientIdFromPath";
import { CareRecipientClassification, careRecipientClassificationList } from "../../../../domain/care_recipient/CareRecipientClassification";

export function AdminCareRecipientEditPage() {
    const dispatch = useAppDispatch();

    const careRecipientId = useCareRecipientIdFromPath();

    const careRecipientInfo = useCareRecipientInformation();

    const processing = useSelector((state: State) => state.adminCareRecipient.processing);

    const whenUpdateClicked = useCallback((
        name: CareRecipientName,
        birthday: DateObject,
        houseId: CareRecipientHouseId,
        serviceProviderId: ServiceProviderId,
        dataSyncStartTime: DateTime | null,
        dataSyncEndTime: DateTime | null,
        sensors: string[],
        customerCode: string,
        branchCode: string,
        classification: CareRecipientClassification | null,
        alertPeriod: {
            start: DateObject,
            end: DateObject
        } | null,
        alertPause: boolean | null,
    ) => {
        if (!careRecipientId) return;
        dispatch(
            updateCareRecipientInfoAction({
                careRecipientId: careRecipientId,
                requestData: {
                    name: name,
                    birthday: birthday,
                    houseId: houseId,
                    serviceProviderId: serviceProviderId,
                    dataSyncStartTime: dataSyncStartTime,
                    dataSyncEndTime: dataSyncEndTime,
                    sensors: sensors.map(sensor => getSensorFromValue(sensor)),
                    classification: classification,
                    alertPeriod: alertPeriod,
                    alertPause: alertPause,
                    customerCode: customerCode,
                    branchCode: branchCode,
                },
                onFailure: (errorCode: string) => alert(errorCode  === "CONFLICT" ? "ハウスIDが登録済みです" : "エラーが発生しました")
            })
        );
    }, [dispatch, careRecipientId]);

    const props: CareRecipientEditComponentProps = useMemo(() => {
        return {
            type: "更新",
            name: careRecipientInfo?.careRecipient.name ?? null,
            houseId: careRecipientInfo?.careRecipient.houseId ?? null,
            birthday: careRecipientInfo?.careRecipient.birthday ?? null,
            customerCode: careRecipientInfo?.careRecipient.customerCode ?? null,
            branchCode: careRecipientInfo?.careRecipient.branchCode ?? null,
            serviceProviderId: careRecipientInfo?.serviceProviderId ?? null,
            dataSyncStartTime: careRecipientInfo?.dataSyncStartTime ?? null,
            dataSyncEndTime: careRecipientInfo?.dataSyncEndTime ?? null,
            sensors: careRecipientInfo?.sensors ? careRecipientInfo.sensors.map(sensor => sensor === undefined ? "" : sensor.value) : [],
            classification: careRecipientInfo?.classification ?? null,
            classificationSelectList: careRecipientClassificationList,
            alertPeriod: careRecipientInfo?.alertPeriod ?? null,
            alertPause: careRecipientInfo?.alertPause ?? null,
            whenUpdateClicked: whenUpdateClicked
        };
    }, [careRecipientInfo, whenUpdateClicked]);

    useEffect(() => {
        if (!careRecipientId) return;
        dispatch(getCareRecipientInfoAction(careRecipientId));
    }, [dispatch, careRecipientId]);

    if (!careRecipientInfo || processing) return <Loading />;

    return (
        <div className={"admin_care_recipient_edit_page"}>
            <BackButton />
            <div className={"header"}>
                利用者更新
            </div>
            <CareRecipientEditComponent {...props} />
        </div>
    );
}
